import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnOperationalView } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth';
import { SnFormFieldConfig } from '@lib/form';
import { SnTeamFieldsGeneratorService } from '@lib/teams';
import { t } from '@lib/translation';

@Component({
  selector: 'sn-add-op-view-dialog',
  templateUrl: './add-op-view-dialog.component.html',
  styleUrls: ['./add-op-view-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddOperationalViewDialogComponent implements OnInit, OnDestroy {
  public isLoading: boolean = false;
  public form = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[];
  public readonly isNew: boolean;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SnOperationalView,
    private readonly _dialogRef: MatDialogRef<AddOperationalViewDialogComponent>,
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
    private readonly _teamFieldsGenerator: SnTeamFieldsGeneratorService,
    private readonly _currentUser: SnCurrentUser,
  ) {
    this.isNew = !this.data?.id;
  }

  public ngOnInit(): void {
    this._setForm();
    this._patchForm();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this.isLoading = true;
    if (this.formModel.team) {
      this.formModel.isPersonal = false;
    }

    if (this.isNew) {
      this.formModel.user = this._currentUser.id;
      this._operationalViewsAPI
        .createView(this.formModel)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (view) => {
            this.isLoading = false;
            this._dialogRef.close(view);
          },
          error: () => {
            this.isLoading = false;
          },
        });
    } else {
      this._operationalViewsAPI
        .updateView(this.data.id, this.formModel)
        .pipe(takeUntil(this._destroy$))
        .subscribe((view) => {
          this.isLoading = false;
          this._dialogRef.close(view);
        });
    }
  }

  public close(): void {
    this._dialogRef.close();
  }

  private _patchForm(): void {
    if (!this.isNew) {
      this.formModel = this.data.toJSON({ groups: ['form'] });
    }
  }

  private _setForm(): void {
    this.formFields = [
      {
        key: 'name',
        type: 'input',
        templateOptions: {
          label: t('dashboard.operational.dialogs.enterViewName'),
          required: true,
        },
      },
      this._teamFieldsGenerator.getTeamSelectField({
        templateOptions: {
          label: t('common.selectTeam'),
          emptyLabel: t('common.none'),
          searchPlaceholder: t('common.search'),
          addButtonText: t('common.add'),
        },
        expressionProperties: {
          'templateOptions.disabled': () => this.formModel.isPersonal,
        },
      }),
      {
        key: 'isPersonal',
        type: 'checkbox',
        templateOptions: {
          label: t('dashboard.operational.dialogs.makeViewPrivate'),
        },
        expressionProperties: {
          'templateOptions.disabled': () => this.formModel.team,
        },
      },
    ];
  }
}
